import { useEffect, useRef } from "react";

const DEFAULT_TITLE = "QuotedsArrow — Literary Quote Discovery";
const DEFAULT_DESC =
  "Discover and save the world's most compelling literary quotes, matched to your taste. 34,000+ curated quotes from philosophy, literature, science, and history.";
const DEFAULT_OG_TITLE = "QuotedsArrow — Literary Quote Discovery";
const DEFAULT_OG_DESC =
  "Discover and save the world's most compelling literary quotes, matched to your taste.";
const DEFAULT_OG_URL = "https://quotedsarrow.com/";

function setMeta(selector: string, value: string) {
  const el = document.querySelector(selector) as HTMLMetaElement | null;
  if (el) el.content = value;
}

function getMeta(selector: string): string {
  const el = document.querySelector(selector) as HTMLMetaElement | null;
  return el?.content ?? "";
}

function setCanonical(href: string) {
  const el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (el) el.href = href;
}

function getCanonical(): string {
  const el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  return el?.href ?? "";
}

/**
 * Updates document.title and the site-wide meta/OG tags for the current page.
 * Restores the index.html defaults on unmount.
 *
 * @param title       Browser tab title (and og:title).
 * @param description Optional meta description and og:description override.
 */
export function usePageMeta({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  const prevRef = useRef<{
    title: string;
    desc: string;
    ogTitle: string;
    ogDesc: string;
    ogUrl: string;
  } | null>(null);

  useEffect(() => {
    if (!prevRef.current) {
      prevRef.current = {
        title: document.title,
        desc: getMeta('meta[name="description"]'),
        ogTitle: getMeta('meta[property="og:title"]'),
        ogDesc: getMeta('meta[property="og:description"]'),
        ogUrl: getMeta('meta[property="og:url"]'),
      };
    }

    document.title = title;
    const desc = description ?? DEFAULT_DESC;
    const ogDesc = description ?? DEFAULT_OG_DESC;

    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', ogDesc);
    setMeta('meta[property="og:url"]', window.location.href);
    setCanonical(window.location.href);

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="description"]', DEFAULT_DESC);
      setMeta('meta[property="og:title"]', DEFAULT_OG_TITLE);
      setMeta('meta[property="og:description"]', DEFAULT_OG_DESC);
      setMeta('meta[property="og:url"]', DEFAULT_OG_URL);
      setCanonical(DEFAULT_OG_URL);
    };
  }, [title, description]);
}
